import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Tv2, Film, Compass, ArrowLeft, Loader2, Youtube, RefreshCw,
  Upload, Download, Trash2, Wand2, FolderOpen, Lightbulb, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ElapsedTimer } from "@/components/ElapsedTimer";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const full = (u) => (u ? `${BACKEND}${u}` : null);

export default function Regenerate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const upRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [source, setSource] = useState(null); // { source_id, source_url, frame_image_id }
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("1.5");
  const [duration, setDuration] = useState(5);
  const [resolution, setResolution] = useState("720p");
  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/regen/history");
      setHistory(data.items || []);
    } catch { /* optional */ }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const onPickFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Please choose a video file (mp4, mov or webm)"); return; }
    setUploading(true);
    setSource(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/regen/upload-video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSource(data);
      toast.success("Video uploaded — first frame ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const regenerate = async () => {
    if (!source?.frame_image_id) { toast.error("Upload a video first"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/regen/generate", {
        frame_image_id: source.frame_image_id,
        prompt: prompt.trim(),
        model, duration, resolution,
        source_id: source.source_id,
      });
      const jobId = data.job_id;
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      let url = null;
      for (let i = 0; i < 170; i++) {
        await sleep(5000);
        try {
          const res = await api.get(`/video-job/${jobId}`);
          if (res.data.status === "done") { url = res.data.video_url; break; }
          if (res.data.status === "error") throw new Error(res.data.error || "Regeneration failed");
        } catch (e) {
          if (e?.message && !e?.response) throw e;
        }
      }
      if (!url) throw new Error("Regeneration timed out");
      toast.success("New video ready");
      await loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Regeneration failed");
    } finally {
      setGenerating(false);
    }
  };

  const removeItem = async (id) => {
    try {
      await api.delete(`/regen/history/${id}`);
      setHistory((h) => h.filter((x) => x.item_id !== id));
      toast.success("Removed");
    } catch {
      toast.error("Could not remove");
    }
  };

  const NavBtn = ({ icon: Icon, label, testid, onClick, active, indent }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 rounded-md py-2 text-sm transition-colors ${indent ? "pl-9 pr-3" : "px-3"} ${
        active ? "bg-[#1d2028] text-[#f3f4f6] border border-[#00e5ff]/40" : "text-[#9ca3af] hover:bg-[#14161c] hover:text-[#f3f4f6] border border-transparent"
      }`}
    >
      <Icon className={`h-4 w-4 shrink-0 ${active ? "text-[#00e5ff]" : ""}`} /> {label}
    </button>
  );

  return (
    <div className="grain flex h-screen overflow-hidden bg-[#0b0c10] text-[#f3f4f6]">
      {/* Left rail */}
      <aside className="flex h-full w-72 shrink-0 flex-col border-r border-[#272a35] bg-[#0e1015]">
        <div className="flex items-center gap-2 px-5 h-16 border-b border-[#272a35]">
          <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-md bg-white">
            <img src={`${process.env.PUBLIC_URL}/ytf-logo.png`} alt="YouTube Factory" className="h-full w-full object-contain" />
          </div>
          <span className="font-display font-bold tracking-tight">YouTube Factory <span className="text-[#00e5ff]">AI</span></span>
        </div>
        <div className="p-3 space-y-1">
          <NavBtn icon={LayoutDashboard} label="Dashboard" testid="nav-dashboard" onClick={() => navigate("/dashboard")} />
          <NavBtn icon={Compass} label="Niches" testid="nav-niches" onClick={() => navigate("/niches")} />
          <NavBtn icon={Tv2} label="Channels" testid="nav-channels" onClick={() => navigate("/channels")} />
        </div>
        <div className="px-3">
          <div className="flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-[#f3f4f6]">
            <FolderOpen className="h-4 w-4 text-[#9ca3af]" /> Content
          </div>
          <NavBtn icon={Film} label="Studio" testid="nav-studio" indent onClick={() => navigate("/studio")} />
          <NavBtn icon={Lightbulb} label="Story Ideas" testid="nav-story-ideas" indent onClick={() => toast.info("Story Ideas is coming soon")} />
          <NavBtn icon={Sparkles} label="Funny Animals" testid="nav-funny-animals" indent onClick={() => navigate("/funny-animals")} />
          <NavBtn icon={RefreshCw} label="Regenerate" testid="nav-regenerate" indent active onClick={() => {}} />
        </div>
        <div className="flex-1" />
        <div className="border-t border-[#272a35] p-4">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1d2028] text-[#00e5ff]"><Youtube className="h-4 w-4" /></div>
            )}
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-medium">{user?.name || "Signed in"}</p>
              <p className="truncate text-xs text-[#9ca3af]">{user?.email || ""}</p>
            </div>
            <button onClick={logout} title="Sign out" className="text-[#9ca3af] hover:text-red-400 transition-colors">
              <ArrowLeft className="h-4 w-4 rotate-180" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="z-20 flex h-16 shrink-0 items-center justify-between border-b border-[#272a35] bg-black/60 px-6 backdrop-blur-xl">
          <div>
            <p className="overline !text-white">Content</p>
            <h1 className="font-display text-base font-semibold tracking-tight leading-none mt-0.5">Regenerate a Short</h1>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="mx-auto max-w-4xl space-y-6">
            {/* Upload + controls */}
            <div className="rounded-lg border border-[#272a35] bg-[#0e1015] p-5" data-testid="regen-panel">
              <p className="text-sm text-[#9ca3af]">
                Upload a <span className="text-[#f3f4f6]">9:16 short video</span>. We take its <span className="text-[#f3f4f6]">first frame</span> as the
                starting point, then generate a fresh 9:16 video from your prompt.
              </p>

              <input
                ref={upRef}
                type="file"
                accept="video/mp4,video/quicktime,video/webm,video/*"
                className="hidden"
                data-testid="regen-file-input"
                onChange={(e) => { const f = e.target.files?.[0]; onPickFile(f); e.target.value = ""; }}
              />

              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-[200px_1fr]">
                {/* Source preview */}
                <div>
                  {uploading ? (
                    <div className="flex aspect-[9/16] items-center justify-center rounded-md border border-dashed border-[#272a35] bg-[#14161c]">
                      <Loader2 className="h-6 w-6 animate-spin text-[#00e5ff]" />
                    </div>
                  ) : source ? (
                    <div className="space-y-2">
                      <video
                        data-testid="regen-source-video"
                        src={full(source.source_url)}
                        controls
                        playsInline
                        className="mx-auto aspect-[9/16] w-full rounded-md border border-[#272a35] bg-black object-contain"
                      />
                      <button
                        data-testid="regen-change-video"
                        onClick={() => upRef.current?.click()}
                        className="flex w-full items-center justify-center gap-1.5 rounded-md border border-[#272a35] px-2 py-1.5 text-xs text-[#9ca3af] hover:text-[#00e5ff]"
                      >
                        <Upload className="h-3.5 w-3.5" /> Change video
                      </button>
                    </div>
                  ) : (
                    <button
                      data-testid="regen-upload-btn"
                      onClick={() => upRef.current?.click()}
                      className="flex aspect-[9/16] w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-[#00e5ff]/50 bg-[#14161c] text-[#9ca3af] transition-colors hover:bg-[#00e5ff]/5"
                    >
                      <Upload className="h-7 w-7 text-[#00e5ff]" />
                      <span className="text-sm font-medium text-[#f3f4f6]">Upload 9:16 video</span>
                      <span className="text-[11px]">mp4, mov or webm</span>
                    </button>
                  )}
                </div>

                {/* Prompt + settings */}
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs text-[#9ca3af]">Prompt (describe the new video)</label>
                    <Textarea
                      data-testid="regen-prompt-input"
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="e.g. Slow cinematic push-in, warm golden light, the subject smiles and waves…"
                      className="min-h-[120px] resize-y border-[#272a35] bg-[#14161c] text-[#f3f4f6] placeholder:text-[#6b7280]"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="mb-1 block text-[10px] uppercase tracking-wide text-[#6b7280]">Model</label>
                      <select data-testid="regen-model" value={model} onChange={(e) => setModel(e.target.value)} className="w-full rounded-md border border-[#272a35] bg-[#14161c] px-2 py-2 text-sm text-[#f3f4f6]">
                        <option value="1.5">Seedance 1.5</option>
                        <option value="2.0">Seedance 2.0</option>
                        <option value="2.5">Seedance 2.5</option>
                        <option value="grok">Grok Imagine</option>
                        <option value="runway">Runway (Gen-4)</option>
                        <option value="runway_seedance">Runway · Seedance 2.5</option>
                        <option value="runway_hailuo">Runway · MiniMax 3</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] uppercase tracking-wide text-[#6b7280]">Duration</label>
                      <select data-testid="regen-duration" value={duration} onChange={(e) => setDuration(Number(e.target.value))} className="w-full rounded-md border border-[#272a35] bg-[#14161c] px-2 py-2 text-sm text-[#f3f4f6]">
                        {[3, 5, 8, 10, 12, 15, 20, 30].map((d) => <option key={d} value={d}>{d}s</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-[10px] uppercase tracking-wide text-[#6b7280]">Resolution</label>
                      <select data-testid="regen-resolution" value={resolution} onChange={(e) => setResolution(e.target.value)} className="w-full rounded-md border border-[#272a35] bg-[#14161c] px-2 py-2 text-sm text-[#f3f4f6]">
                        {["480p", "720p", "1080p"].map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  </div>
                  <Button
                    data-testid="regen-generate-btn"
                    onClick={regenerate}
                    disabled={!source || generating}
                    className="w-full rounded-md bg-[#00e5ff] font-semibold text-black hover:bg-[#33ebff] disabled:opacity-50"
                  >
                    {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}
                    {generating ? "Regenerating…" : "Regenerate video"}
                    {generating && <ElapsedTimer testid="regen-video-timer" className="ml-2 font-mono text-sm font-bold tabular-nums text-black" />}
                  </Button>
                </div>
              </div>
            </div>

            {/* History */}
            <div>
              <div className="mb-3 flex items-center gap-2 text-xs text-[#9ca3af]">
                <Sparkles className="h-3.5 w-3.5 text-[#f5b301]" /> Regenerated clips ({history.length})
              </div>
              {history.length === 0 ? (
                <div className="rounded-lg border border-dashed border-[#272a35] bg-[#14161c] p-8 text-center text-sm text-[#9ca3af]" data-testid="regen-history-empty">
                  Your regenerated videos will appear here.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4" data-testid="regen-history">
                  {history.map((it) => (
                    <div key={it.item_id} className="rounded-lg border border-[#272a35] bg-[#0e1015] p-2" data-testid={`regen-item-${it.item_id}`}>
                      <video src={full(it.video_url)} controls playsInline className="mb-2 aspect-[9/16] w-full rounded bg-black object-contain" />
                      <p className="mb-2 line-clamp-2 text-[11px] text-[#9ca3af]" title={it.prompt}>{it.prompt || <span className="italic">No prompt</span>}</p>
                      <div className="mb-2 flex flex-wrap gap-1 text-[10px] text-[#6b7280]">
                        <span className="rounded bg-[#14161c] px-1.5 py-0.5">{it.model === "grok" ? "Grok" : `Seedance ${it.model}`}</span>
                        <span className="rounded bg-[#14161c] px-1.5 py-0.5">{it.duration}s</span>
                        <span className="rounded bg-[#14161c] px-1.5 py-0.5">{it.resolution}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <a
                          href={full(it.video_url)}
                          download={`regenerated-${it.item_id}.mp4`}
                          data-testid={`regen-download-${it.item_id}`}
                          className="flex flex-1 items-center justify-center gap-1 rounded-md border border-[#272a35] px-2 py-1.5 text-xs text-[#f3f4f6] hover:border-[#00e5ff]/50 hover:text-[#00e5ff]"
                        >
                          <Download className="h-3.5 w-3.5" /> Download
                        </a>
                        <button
                          data-testid={`regen-delete-${it.item_id}`}
                          onClick={() => removeItem(it.item_id)}
                          className="rounded-md border border-[#272a35] p-1.5 text-[#9ca3af] hover:border-red-500/50 hover:text-red-400"
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
